'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createGlossaryEntryService,
  deleteGlossaryEntryService,
  updateGlossaryEntryService,
} from '@/services/httpServices/GlossaryServices';
import type {
  CreateGlossaryEntryServiceConfig,
  DeleteGlossaryEntryServiceConfig,
  UpdateGlossaryEntryServiceConfig,
} from '@/services/httpServices/GlossaryServices/types';

import { glossaryEntryByIdKeys, glossaryListKeys } from '../queries/glossaryQueryKeys';

/**
 * Mutation hook for creating a new glossary entry
 */
export function useCreateGlossaryEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateGlossaryEntryServiceConfig) => {
      try {
        const response = await createGlossaryEntryService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate all glossary list queries using hierarchical keys
      await queryClient.invalidateQueries({
        queryKey: glossaryListKeys(),
      });

      // No need to invalidate a specific glossary entry by ID since this is a new entry
    },
  });
}

/**
 * Mutation hook for updating an existing glossary entry
 */
export function useUpdateGlossaryEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateGlossaryEntryServiceConfig) => {
      try {
        const response = await updateGlossaryEntryService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate all glossary list queries using hierarchical keys
      await queryClient.invalidateQueries({
        queryKey: glossaryListKeys(),
      });

      // Invalidate the specific glossary entry by ID query
      await queryClient.invalidateQueries({
        queryKey: glossaryEntryByIdKeys(variables),
      });
    },
  });
}

/**
 * Mutation hook for deleting a glossary entry
 */
export function useDeleteGlossaryEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DeleteGlossaryEntryServiceConfig) => {
      try {
        await deleteGlossaryEntryService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Only invalidate glossary list queries - don't invalidate the specific entry
      // that was just deleted, as this causes unnecessary refetches
      await queryClient.invalidateQueries({
        queryKey: glossaryListKeys(),
      });
    },
  });
}
